import { useEffect, useRef } from 'react';

import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { DefaultValues, FieldValues, useForm, UseFormReturn } from 'react-hook-form';

import { useFlowNavigation } from './updateContextAndGoToNextStep';
import { FlowConsumerContextEnergy } from '../context';

type FlowStepSchema = Parameters<typeof yupResolver>[0];
type FlowStepMode = 'onSubmit' | 'onChange' | 'onTouched' | 'onBlur' | 'all';

interface UseFlowStepOptions<TFields extends FieldValues> {
  buildSchema: (context: FlowConsumerContextEnergy) => FlowStepSchema;
  defaultValues: (context: FlowConsumerContextEnergy) => DefaultValues<TFields>;
  mode?: FlowStepMode;
  onSubmitSuccess: (formData: TFields, context: FlowConsumerContextEnergy) => Partial<FlowConsumerContextEnergy> | void;
}

interface UseFlowStepResult<TFields extends FieldValues> {
  context: FlowConsumerContextEnergy;
  form: UseFormReturn<TFields>;
}

/**
 * Bridges a step's react-hook-form instance to the flow machine: builds the
 * form with the given schema/defaults, and on successful submit hands the
 * transformed values to `updateContextAndGoToNextStep`. Shared across the
 * energyFlow step screens, which otherwise repeat this wiring verbatim.
 */
export function useFlowStep<TFields extends FieldValues>({
  buildSchema,
  defaultValues,
  mode = 'onSubmit',
  onSubmitSuccess,
}: UseFlowStepOptions<TFields>): UseFlowStepResult<TFields> {
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const { context } = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();

  const schemaRef = useRef(buildSchema(context));
  schemaRef.current = buildSchema(context);

  const form = useForm<TFields>({
    mode,
    defaultValues: defaultValues(context),
    resolver: (values, resolverContext, options) =>
      typedYupResolver<TFields>(schemaRef.current)(values, resolverContext, options),
  });

  const {
    getValues,
    formState: { isSubmitSuccessful, errors },
  } = form;

  // Touch proxy fields inside this hook so consumers always re-render on submit errors.
  void errors;

  useEffect(() => {
    if (isSubmitSuccessful) {
      const values = onSubmitSuccess(getValues(), context);

      if (values) {
        updateContextAndGoToNextStep(values);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return { context, form };
}
